<template>
    <div class="federation-tab md-layout">
        <div class="md-layout-item md-size-100" v-if="loading">
            <p class="md-body-1">Loading federation settings…</p>
        </div>
        <template v-else>
            <div class="md-layout-item md-size-100" v-if="!hasProvider">
                <p class="md-body-1">No federation provider is configured for this organization yet.</p>
                <md-button class="md-primary" :disabled="saving" @click="generateUrls">
                    Generate URLs
                </md-button>
            </div>

            <div class="md-layout-item md-size-100" v-else>
                <div v-if="credentialsComplete" class="federation-enable-row">
                    <md-switch
                        class="federation-enabled-switch"
                        v-model="editForm.federation_enabled"
                        :disabled="saving || loading"
                        @change="onFederationEnabledChange"
                    >Federation enabled</md-switch>
                </div>

                <h3 class="federation-segment-title">Customer Configuration</h3>
                <table class="federation-segment-table" v-if="credentialsComplete && !isEditing">
                    <tbody>
                    <tr>
                        <th>Provider</th>
                        <td>{{ supportedProvider }}</td>
                    </tr>
                    <tr>
                        <th>Client ID</th>
                        <td>{{ editForm.client_id || '—' }}</td>
                    </tr>
                    <tr>
                        <th>Tenant</th>
                        <td>{{ editForm.tenant || '—' }}</td>
                    </tr>
                    <tr>
                        <th>Client Secret</th>
                        <td>{{ editForm.client_secret_hint || editForm.client_secret_masked || '—' }}</td>
                    </tr>
                    </tbody>
                </table>
                <div class="md-layout md-gutter" v-else>
                    <div class="md-layout-item md-small-size-100 md-medium-size-33">
                        <md-field class="md-has-value">
                            <label>Provider</label>
                            <md-input :value="supportedProvider" disabled autocomplete="off"/>
                        </md-field>
                    </div>
                    <div class="md-layout-item md-small-size-100 md-medium-size-33">
                        <md-field class="md-has-value">
                            <label>Client ID</label>
                            <md-input v-model="editForm.client_id" autocomplete="off"/>
                        </md-field>
                    </div>
                    <div class="md-layout-item md-small-size-100 md-medium-size-33">
                        <md-field class="md-has-value">
                            <label>Tenant</label>
                            <md-input v-model="editForm.tenant" autocomplete="off"/>
                        </md-field>
                    </div>
                    <div class="md-layout-item md-small-size-100 md-medium-size-33 federation-client-secret-wrap">
                        <md-field class="md-has-value" :md-toggle-password="false">
                            <label>Client Secret</label>
                            <md-input v-model="editForm.client_secret" type="password" autocomplete="new-password"/>
                        </md-field>
                        <p v-if="credentialsComplete" class="federation-client-secret-hint">Leave blank to keep the current secret.</p>
                    </div>
                </div>

                <h3 class="federation-segment-title">Navitas Settings</h3>
                <table class="federation-segment-table">
                    <tbody>
                    <tr>
                        <th>Redirect URL</th>
                        <td>
                            <div class="federation-url-with-copy">
                                <span class="federation-url-cell">{{ editForm.redirect_url }}</span>
                                <md-button
                                    type="button"
                                    class="md-icon-button md-dense federation-copy-btn"
                                    title="Copy to clipboard"
                                    aria-label="Copy to clipboard"
                                    :disabled="!editForm.redirect_url"
                                    @click="copyToClipboard(editForm.redirect_url)"
                                >
                                    <md-icon>content_copy</md-icon>
                                </md-button>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <th>Callback URL</th>
                        <td>
                            <div class="federation-url-with-copy">
                                <span class="federation-url-cell">{{ editForm.callback_url }}</span>
                                <md-button
                                    type="button"
                                    class="md-icon-button md-dense federation-copy-btn"
                                    title="Copy to clipboard"
                                    aria-label="Copy to clipboard"
                                    :disabled="!editForm.callback_url"
                                    @click="copyToClipboard(editForm.callback_url)"
                                >
                                    <md-icon>content_copy</md-icon>
                                </md-button>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </template>
    </div>
</template>

<script>
import alerts from '../../../js/alerts';

const SUPPORTED_PROVIDER = 'azure';

export default {
    name: 'OrganizationTabFederation',
    data() {
        return {
            supportedProvider: SUPPORTED_PROVIDER,
            loading: false,
            saving: false,
            hasProvider: false,
            credentialsComplete: false,
            isEditing: false,
            editForm: {
                federation_enabled: false,
                client_id: '',
                client_secret: '',
                client_secret_masked: '',
                client_secret_hint: '',
                tenant: '',
                redirect_url: '',
                callback_url: '',
            },
            serverBaseline: {
                client_id: '',
                tenant: '',
            },
        };
    },
    computed: {
        organizationId() {
            return this.$route.params.id;
        },
    },
    watch: {
        '$route.params.tab'(tab) {
            if (tab === 'federation') {
                this.loadFederation();
            }
        },
        isEditing() {
            this.emitToolbarState();
        },
        saving() {
            this.emitToolbarState();
        },
        hasProvider() {
            this.emitToolbarState();
        },
        loading() {
            this.emitToolbarState();
        },
        credentialsComplete() {
            this.emitToolbarState();
        },
    },
    mounted() {
        if (this.$route.params.tab === 'federation') {
            this.loadFederation();
        } else {
            this.$nextTick(() => this.emitToolbarState());
        }
    },
    methods: {
        emitToolbarState() {
            this.$emit('federation-toolbar', {
                hasProvider: this.hasProvider,
                credentialsPending: this.hasProvider && !this.credentialsComplete,
                isEditing: this.isEditing,
                saving: this.saving,
                loading: this.loading,
            });
        },
        orgSegments() {
            return {organizationId: this.organizationId};
        },
        getTrimmedClientId() {
            return (this.editForm.client_id || '').trim();
        },
        getTrimmedClientSecret() {
            return (this.editForm.client_secret || '').trim();
        },
        getTrimmedTenant() {
            return (this.editForm.tenant || '').trim();
        },
        showValidationErrors(messages) {
            if (!Array.isArray(messages) || messages.length === 0) {
                return;
            }
            messages.forEach((msg) => alerts.error(msg));
        },
        collectRequiredFieldErrors() {
            const errors = [];
            if (!this.getTrimmedClientId()) {
                errors.push('Client ID is required');
            }
            if (!this.getTrimmedTenant()) {
                errors.push('Tenant ID is required');
            }
            return errors;
        },
        collectPendingCredentialErrors() {
            const errors = this.collectRequiredFieldErrors();
            if (!this.getTrimmedClientSecret()) {
                errors.push('Client Secret is required');
            }
            return errors;
        },
        collectApiValidationErrors(body) {
            const errors = body && body.errors;
            if (!errors || typeof errors !== 'object') {
                return [];
            }
            const messages = [];
            Object.keys(errors).forEach((key) => {
                const fieldMessages = errors[key];
                if (Array.isArray(fieldMessages)) {
                    fieldMessages.forEach((msg) => messages.push(msg));
                }
            });
            return messages;
        },
        validateEditCredentials() {
            const errors = this.collectRequiredFieldErrors();
            this.showValidationErrors(errors);
            return errors.length === 0;
        },
        validatePendingCredentials() {
            const errors = this.collectPendingCredentialErrors();
            this.showValidationErrors(errors);
            return errors.length === 0;
        },
        handleFederationSaveError(err, fallbackMessage = 'Failed to save federation settings') {
            const res = err && err.response;
            const body = res && res.data;
            const messages = body && body.messages;
            if (Array.isArray(messages) && messages.length > 0) {
                return;
            }
            const apiErrors = this.collectApiValidationErrors(body);
            if (apiErrors.length > 0) {
                this.showValidationErrors(apiErrors);
                return;
            }
            this.showValidationErrors([
                (body && body.message) || err.message || fallbackMessage,
            ]);
        },
        handleFederationGenerateUrlsError(err) {
            const status = err.response && err.response.status;
            if (status === 409) {
                this.loadFederation();
            }
            this.handleFederationSaveError(err, 'Failed to generate federation URLs');
        },
        applyPayload(data) {
            this.hasProvider = true;
            this.credentialsComplete = Boolean(data.credentials_complete);
            this.isEditing = !this.credentialsComplete;
            this.editForm.client_id = data.client_id || '';
            this.editForm.federation_enabled = Boolean(data.federation_enabled);
            this.editForm.client_secret = '';
            this.editForm.client_secret_masked = data.client_secret_masked || '';
            this.editForm.client_secret_hint = data.client_secret_hint || data.client_secret_masked || '';
            this.editForm.tenant = data.tenant || '';
            this.editForm.redirect_url = data.redirect_url || '';
            this.editForm.callback_url = data.callback_url || '';
            this.serverBaseline = {
                client_id: data.client_id || '',
                tenant: data.tenant || '',
            };
        },
        loadFederation() {
            this.loading = true;
            this.$api.organizationFederationShow({segments: this.orgSegments()})
                .then(({data}) => {
                    this.applyPayload(data);
                })
                .catch((err) => {
                    const status = err.response && err.response.status;
                    if (status === 404) {
                        this.hasProvider = false;
                        this.credentialsComplete = false;
                        this.isEditing = false;
                        return;
                    }
                    throw err;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        generateUrls() {
            this.saving = true;
            this.$api.organizationFederationGenerateUrls({segments: this.orgSegments()})
                .then(({data}) => {
                    this.applyPayload(data);
                })
                .catch((err) => this.handleFederationGenerateUrlsError(err))
                .finally(() => {
                    this.saving = false;
                });
        },
        startEdit() {
            if (!this.credentialsComplete) {
                return;
            }
            this.isEditing = true;
            this.editForm.client_secret = '';
        },
        cancelEdit() {
            if (!this.credentialsComplete) {
                return;
            }
            this.isEditing = false;
            this.editForm.client_id = this.serverBaseline.client_id;
            this.editForm.tenant = this.serverBaseline.tenant;
            this.editForm.client_secret = '';
        },
        submitPendingCredentials() {
            if (!this.validatePendingCredentials()) {
                return;
            }
            this.saving = true;
            this.$api.organizationFederationUpdate({
                segments: this.orgSegments(),
                data: {
                    provider: SUPPORTED_PROVIDER,
                    client_id: this.getTrimmedClientId(),
                    client_secret: this.getTrimmedClientSecret(),
                    tenant: this.getTrimmedTenant(),
                },
            })
                .then(({data}) => {
                    this.applyPayload(data);
                })
                .catch((err) => this.handleFederationSaveError(err))
                .finally(() => {
                    this.saving = false;
                });
        },
        submitUpdate() {
            if (!this.credentialsComplete) {
                this.submitPendingCredentials();
                return;
            }

            if (!this.validateEditCredentials()) {
                return;
            }

            const payload = {};
            const clientId = this.getTrimmedClientId();
            const baselineClient = (this.serverBaseline.client_id || '').trim();
            if (clientId !== baselineClient) {
                payload.client_id = clientId;
            }
            const tenant = this.getTrimmedTenant();
            const baselineTenant = (this.serverBaseline.tenant || '').trim();
            if (tenant !== baselineTenant) {
                payload.tenant = tenant;
            }
            const clientSecret = this.getTrimmedClientSecret();
            if (clientSecret) {
                payload.client_secret = clientSecret;
            }
            if (Object.keys(payload).length === 0) {
                alerts.info('No changes made');
                return;
            }
            this.saving = true;
            this.$api.organizationFederationUpdate({
                segments: this.orgSegments(),
                data: payload,
            })
                .then(({data}) => {
                    this.applyPayload(data);
                })
                .catch((err) => this.handleFederationSaveError(err))
                .finally(() => {
                    this.saving = false;
                });
        },
        onFederationEnabledChange(enabled) {
            this.setFederationEnabled(enabled);
        },
        setFederationEnabled(enabled) {
            this.saving = true;
            this.$api.organizationFederationPatch({
                segments: this.orgSegments(),
                data: {federation_enabled: enabled},
            })
                .then(({data}) => {
                    this.applyPayload(data);
                })
                .catch((err) => {
                    this.editForm.federation_enabled = !enabled;
                    this.handleFederationSaveError(err, 'Failed to update federation status');
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        copyToClipboard(text) {
            const value = text == null ? '' : String(text);
            if (!value) {
                return;
            }
            if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                navigator.clipboard.writeText(value).catch(() => this.fallbackCopyToClipboard(value));
            } else {
                this.fallbackCopyToClipboard(value);
            }
        },
        fallbackCopyToClipboard(value) {
            const ta = document.createElement('textarea');
            ta.value = value;
            ta.setAttribute('readonly', '');
            ta.style.position = 'fixed';
            ta.style.left = '-9999px';
            document.body.appendChild(ta);
            ta.select();
            try {
                document.execCommand('copy');
            } catch (e) {
                // ignore
            }
            document.body.removeChild(ta);
        },
    },
};
</script>

<style scoped>
.federation-segment-title {
    margin: 20px 0 12px;
    font-size: 1rem;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.65);
}

.federation-enabled-switch {
    margin-top: 8px;
    margin-bottom: 8px;
}

.federation-segment-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 8px;
}

.federation-segment-table th,
.federation-segment-table td {
    border: 1px solid rgba(0, 0, 0, 0.12);
    padding: 10px 12px;
    text-align: left;
    vertical-align: top;
}

.federation-segment-table th {
    width: 140px;
    background: rgba(0, 0, 0, 0.04);
    font-weight: 600;
}

.federation-url-with-copy {
    display: flex;
    align-items: flex-start;
    gap: 4px;
}

.federation-url-with-copy .federation-url-cell {
    flex: 1;
    min-width: 0;
    word-break: break-all;
    font-family: monospace;
    font-size: 0.85rem;
}

.federation-copy-btn {
    flex-shrink: 0;
    margin: 0;
}

.federation-client-secret-wrap >>> .md-field {
    margin-bottom: 4px;
}

.federation-client-secret-hint {
    margin: 0 0 8px;
    font-size: 0.75rem;
    line-height: 1.35;
    color: rgba(0, 0, 0, 0.54);
}
</style>
